define('SageDotCom/Components/Content/FixedScroll',[
    "jquery",
    "SageDotCom/DataLayer/tealium",
    "SageDotCom/DataLayer/Events/sectionVisibility"
], function ($, tealium, sectionVisibilityEvent) {

    function FixedScroll(scope) {
        let isInitialized = false;
        let resizeTimeout = null;

        const container = scope.querySelector("[data-scroll-container]");
        if (!container) return;

        const scoreCenter = container.querySelector(".score-center");
        if (!scoreCenter) return;

        const mobileBreakpoint = 1080;

        let panelMap = [];
        let textCol = null;
        let mediaCol = null;
        let textSections = [];
        let mediaItems = [];
        let currentStepIndex = 0;
        let intersectionObserver = null;
        let animationFrameId = null;

        function shouldActivateParallax() {
            return window.innerWidth >= mobileBreakpoint &&
                !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        }

        function finalizeLazyAnimations() {
            const items = scoreCenter.querySelectorAll(".flex-item");
            for (let i = 0; i < items.length; i++) {
                items[i].classList.remove("lazy");
                items[i].classList.add("lazy-animated--finished");
            }
        }

        //---------------------
        // Init parallax and modify content
        //---------------------
        function initializeParallax() {
            if (isInitialized) return;

            const panels = scoreCenter.querySelectorAll(".score-flex-box.col-2-wide-right");
            if (!panels.length) return;

            panelMap = [];

            for (let i = 0; i < panels.length; i++) {
                const items = panels[i].querySelectorAll(".flex-item");
                if (items.length < 2) continue;

                panelMap.push({
                    panel: panels[i],
                    text: items[0],
                    media: items[1]
                });
            }

            if (!panelMap.length) return;

            textCol = document.createElement("div");
            textCol.className = "fixed-scroll__text";

            mediaCol = document.createElement("div");
            mediaCol.className = "fixed-scroll__media";

            scoreCenter.appendChild(textCol);
            scoreCenter.appendChild(mediaCol);
            scoreCenter.classList.add("fixed-scroll-active");

            textSections = [];
            mediaItems = [];

            for (let i = 0; i < panelMap.length; i++) {
                const section = document.createElement("div");
                section.className = "fixed-scroll__section";
                section.setAttribute("data-section-index", i);

                section.appendChild(panelMap[i].text);
                textCol.appendChild(section);

                mediaCol.appendChild(panelMap[i].media);

                textSections.push(section);
                mediaItems.push(panelMap[i].media);
            }

            for (let j = 0; j < panelMap.length; j++) {
                panelMap[j].panel.parentNode.removeChild(panelMap[j].panel);
            }

            setupInitialState();
            setupIntersectionObserver();

            window.addEventListener("scroll", onScroll, { passive: true });

            finalizeLazyAnimations();
            isInitialized = true;
        }

        //---------------------
        // Destroy parallax and restore original content
        //---------------------
        function destroyParallax() {
            if (!isInitialized) return;

            window.removeEventListener("scroll", onScroll);

            if (intersectionObserver) {
                intersectionObserver.disconnect();
                intersectionObserver = null;
            }

            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
                animationFrameId = null;
            }

            if (textCol && textCol.parentNode) {
                textCol.parentNode.removeChild(textCol);
            }

            if (mediaCol && mediaCol.parentNode) {
                mediaCol.parentNode.removeChild(mediaCol);
            }

            for (let i = 0; i < panelMap.length; i++) {
                panelMap[i].panel.appendChild(panelMap[i].text);
                panelMap[i].panel.appendChild(panelMap[i].media);
                scoreCenter.appendChild(panelMap[i].panel);
            }

            scoreCenter.classList.remove("fixed-scroll-active", "is-parallax-active");

            finalizeLazyAnimations();

            panelMap = [];
            textSections = [];
            mediaItems = [];
            textCol = null;
            mediaCol = null;
            currentStepIndex = 0;
            isInitialized = false;
        }

        function setupInitialState() {
            if (!textSections.length) return;

            textSections[0].classList.add("is-active");
            mediaItems[0].classList.add("is-active");

            for (let i = 0; i < mediaItems.length; i++) {
                const focusables = mediaItems[i].querySelectorAll(
                    "video, iframe, lite-youtube, button, [role='button']"
                );

                for (let j = 0; j < focusables.length; j++) {
                    focusables[j].setAttribute("tabindex", i === 0 ? "0" : "-1");
                }
            }

            const heading = textSections[0].querySelector("h1, h2, h3");
            triggerSectionVisibilityEvent(0, heading ? heading.textContent.trim() : "");
        }

        function setupIntersectionObserver() {
            intersectionObserver = new IntersectionObserver(function (entries) {
                for (let i = 0; i < entries.length; i++) {
                    if (!entries[i].isIntersecting) continue;
                    const index = textSections.indexOf(entries[i].target);
                    if (index !== -1) updateActiveSection(index);
                }
            }, {
                threshold: 0.5,
                rootMargin: "-15% 0px -15% 0px"
            });

            for (let i = 0; i < textSections.length; i++) {
                intersectionObserver.observe(textSections[i]);
            }
        }

        function updateActiveSection(index) {
            if (index === currentStepIndex) return;
            currentStepIndex = index;

            for (let i = 0; i < textSections.length; i++) {
                textSections[i].classList.toggle("is-active", i === index);
            }

            for (let i = 0; i < mediaItems.length; i++) {
                mediaItems[i].classList.toggle("is-active", i === index);

                const focusables = mediaItems[i].querySelectorAll(
                    "video, iframe, lite-youtube, button, [role='button']"
                );

                for (let j = 0; j < focusables.length; j++) {
                    focusables[j].setAttribute("tabindex", i === index ? "0" : "-1");
                }
            }

            const heading = textSections[index].querySelector("h1, h2, h3");
            triggerSectionVisibilityEvent(index, heading ? heading.textContent.trim() : "");
        }

        function onScroll() {
            if (animationFrameId) return;

            animationFrameId = requestAnimationFrame(function () {
                const viewportCenter = window.innerHeight / 2;
                let closestIndex = 0;
                let minDistance = Infinity;

                for (let i = 0; i < textSections.length; i++) {
                    const rect = textSections[i].getBoundingClientRect();
                    const center = rect.top + rect.height / 2;
                    const distance = Math.abs(center - viewportCenter);

                    if (distance < minDistance) {
                        minDistance = distance;
                        closestIndex = i;
                    }
                }

                updateActiveSection(closestIndex);
                animationFrameId = null;
            });
        }

        //---------------------
        // Handle viewport/preference changes
        //---------------------
        function handleEnvironmentChange() {
            if (resizeTimeout) clearTimeout(resizeTimeout);

            resizeTimeout = setTimeout(function () {
                const shouldBeActive = shouldActivateParallax();

                if (shouldBeActive && !isInitialized) {
                    initializeParallax();
                } else if (!shouldBeActive && isInitialized) {
                    destroyParallax();
                }
            }, 250);
        }

        window.addEventListener("resize", handleEnvironmentChange, { passive: true });

        const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        motionQuery.addEventListener("change", handleEnvironmentChange);

        if (shouldActivateParallax()) {
            initializeParallax();
        }

        this.destroy = function () {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            window.removeEventListener("resize", handleEnvironmentChange);
            motionQuery.removeEventListener("change", handleEnvironmentChange);
            destroyParallax();
        };
    }

    function triggerSectionVisibilityEvent(index, heading) {
        const event = sectionVisibilityEvent.GenerateEventMetadata(index + 1, heading || "");
        tealium.event(event);
    }

    FixedScroll.init = function (args) {
        return args.IsEditorEditing === true ? null : new FixedScroll(args.scope);
    };

    return FixedScroll;
});

